// ====================================================================================================
//  File Name   : DevicesPage.js
//  Location    : src/features/devices/DevicesPage.js
//  Project     : AIRS Security - Autonomous Sentinel Ecosystem
//  Developed By: Appence Software Solution | https://appence.com
//  Legal Notice: Proprietary Property. Unauthorized use strictly prohibited. (c) 2026
// ====================================================================================================

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import { 
  Search, Monitor, Smartphone, Server, 
  Laptop, Trash2 
} from 'lucide-react';

const OSIcon = ({ os }) => {
  const lowerOS = (os || '').toLowerCase();
  if (lowerOS.includes('windows')) return <Monitor className="w-5 h-5 text-blue-500" />;
  if (lowerOS.includes('mac') || lowerOS.includes('darwin')) return <Laptop className="w-5 h-5 text-slate-500" />;
  if (lowerOS.includes('android') || lowerOS.includes('ios')) return <Smartphone className="w-5 h-5 text-green-500" />;
  return <Server className="w-5 h-5 text-orange-500" />; // Linux/Server default
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    status === 'online' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
      status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-slate-400'
    }`}></span>
    {status.toUpperCase()}
  </span>
);

const DevicesPage = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  // Fetch Devices with filters
  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['devices', search, statusFilter],
    queryFn: () => axiosClient.get('/admin/devices', {
      params: { search, status: statusFilter }
    }),
    keepPreviousData: true,
    refetchInterval: 5000, // Live updates
  });

  // Delete Agent Mutation
  const deleteMutation = useMutation({
    mutationFn: (agentId) => axiosClient.delete(`/admin/devices/${agentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
    },
    onError: (err) => {
      alert(`Failed to delete device: ${err?.response?.data?.detail || err.message}`);
    }
  });

  const handleDeleteDevice = (agentId, hostname) => {
    const confirmMessage = `Are you sure you want to purge device "${hostname}" (${agentId})?\n\nThis will permanently delete all associated alerts, telemetry, and device history.`;
    if (window.confirm(confirmMessage)) {
      deleteMutation.mutate(agentId);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Device Fleet</h1>
          <p className="text-slate-500 text-sm">Manage and monitor all deployed agents.</p>
        </div>
        
        <div className="flex space-x-2 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:flex-initial">
            <input
              type="text"
              placeholder="Search hostname or IP..."
              className="w-full sm:w-64 pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          </div>

          {/* Filter */}
          <select 
            className="pl-3 pr-8 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
        </div>
      </div>

      {/* Device Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-6 py-4">Device Name</th>
              <th className="px-6 py-4">OS / Platform</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Last Seen</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {isLoading ? (
               <tr><td colSpan="5" className="p-6 text-center text-slate-400">Scanning fleet...</td></tr>
            ) : devices.length === 0 ? (
               <tr><td colSpan="5" className="p-6 text-center text-slate-400">No devices found matching criteria.</td></tr>
            ) : (
              devices.map((device) => (
                <tr key={device.agent_id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center mr-3 text-slate-500">
                        <OSIcon os={device.os_info} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700">{device.hostname}</p>
                        <p className="text-xs text-slate-400 font-mono">{device.agent_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {device.os_info || 'Unknown OS'}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={device.status} />
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Link 
                        to={`/devices/${device.agent_id}`}
                        className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors"
                      >
                        View Details
                      </Link>
                      
                      {/* Delete Device Action Button */}
                      <button
                        onClick={() => handleDeleteDevice(device.agent_id, device.hostname)}
                        disabled={deleteMutation.isLoading}
                        className="inline-flex items-center p-1.5 bg-white border border-slate-200 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-50"
                        title="Purge Device & Telemetry Data"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DevicesPage;